import * as React from "react";
import { FC, PropsWithChildren } from "react";
import AckLogger from "../common/AckLogger";
import { Auth0Provider, useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import { Auth0ContextInterface } from "@auth0/auth0-react/src/auth0-context";

const AUTH0_DOMAIN = "ackbx.us.auth0.com";
const AUTH0_CLIENT_ID = "Vgwb13otmx4gT4tzJ7niG3NwDhwFU2IY";

export type AckAuthProviderProps = {
  audience?: string;
  scope?: string;
}

export const AckAuthProvider: FC<PropsWithChildren<AckAuthProviderProps>> = (props) => {
  const params = {
    redirect_uri: window.location.origin,
    audience: props.audience,
    scope: props.scope,
  };
  return (
    <Auth0Provider
      domain={AUTH0_DOMAIN}
      clientId={AUTH0_CLIENT_ID}
      authorizationParams={params}
    >
      {props.children}
    </Auth0Provider>
  );
};

interface AckAuthService {
  isAuthenticated: boolean;

  userId?: string;

  username?: string;

  userPicture?: string;

  signIn(options?: object): Promise<void>;

  signOut(options?: object): void;

  getAccessToken(scope?: string): Promise<string>;
}

class AckAuth0Service implements AckAuthService {
  private readonly auth0: Auth0ContextInterface;

  constructor(auth0: Auth0ContextInterface) {
    this.auth0 = auth0;
  }

  get userId(): string | undefined {
    if (this.auth0.user && this.auth0.user.sub) {
      return this.hashCode(this.auth0.user.sub);
    }
    return undefined;
  }

  get username(): string | undefined {
    if (!this.auth0) {
      AckLogger.warn("No auth0 object found");
      return undefined;
    }
    if (!this.auth0.user) {
      AckLogger.warn("No user object found");
      return undefined;
    }
    if (this.auth0.user.preferred_username) {
      return this.auth0.user.preferred_username;
    }
    AckLogger.warn("No username found");

    if (this.auth0.user.nickname) {
      return this.auth0.user.nickname;
    }
    AckLogger.warn("No nickname found");

    if (this.auth0.user.email) {
      return this.auth0.user.email;
    }
    AckLogger.warn("No email found");
    return undefined;
  }

  get userPicture(): string | undefined {
    if (this.auth0.user) {
      return this.auth0.user.picture;
    }
    return undefined;
  }

  get isAuthenticated(): boolean {
    const authenticated = this.auth0.isAuthenticated;
    AckLogger.info("User isAuthenticated", authenticated);
    return authenticated;
  }

  validateState() {
    if (this.auth0.isLoading) {
      AckLogger.warn("Authentication is still loading");
    }
    if (this.auth0.error) {
      AckLogger.warn(`Error while authenticating: [${this.auth0.error.message}]`);
    }
  }

  signIn(options?: object): Promise<void> {
    this.validateState();
    return this.auth0.loginWithRedirect(options);
  }

  signOut(options?: object): Promise<void> {
    return this.auth0.logout(options);
  }

  getAccessToken(scope?: string): Promise<string> {
    this.validateState();
    return this.auth0.getAccessTokenSilently({ authorizationParams: { scope: scope } });
  }

  private hashCode = (s: string): string => {
    return Buffer.from(s).toString("base64");
  };
}

export const withPrivateRoute = (component: any, returnTo?: () => string): any => {
  return withAuthenticationRequired(component, {
    onRedirecting: () => (<section className="section"><p>Redirecting you to the login page...</p></section>),
    returnTo: returnTo || "#",
  });
};

export const useAuth = (): AckAuthService => {
  const context = useAuth0();
  return new AckAuth0Service(context);
};

export const useAuthFetch = () => {
  const auth = useAuth();

  const validateAuthResponse = (response: Response) => {
    if (response.status === 401) { // Unauthorized
      throw new Error("Operation not authorized");
    }
    if (response.status === 403) { // Forbidden
      throw new Error("Operation is not permitted");
    }
    return response;
  };

  return (url: string, options: RequestInit): Promise<Response> => {
    return auth.getAccessToken()
      .then(token => {
        const headers = { ...options.headers, "Authorization": `Bearer ${token}` };
        const requestOptions = { ...options, headers: headers };
        return fetch(url, requestOptions)
          .then(validateAuthResponse)
          .catch(err => {
            throw new Error(`Error while communicating with server: [${err.message}]`);
          });
      })
      .catch(err => {
        throw new Error(`Error acquiring access token: [${err.message}]`);
      });
  };
};
